import type { PropsWithChildren } from 'react';
import { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { Hyperlink } from '@THC/components/Hyperlink';

import { TOGGLE_TEXT } from './CollapsableText.const';

import styles from './CollapsableText.module.scss';

type CollapsableTextProps = {
	linesToShow?: number;
	showLessText?: string;
	showMoreText?: string;
};

const { showLess, showMore } = TOGGLE_TEXT;

export const CollapsableText = ({
	children,
	linesToShow = 3,
	showLessText = showLess,
	showMoreText = showMore,
}: PropsWithChildren<CollapsableTextProps>) => {
	const contentRef = useRef<HTMLDivElement>(null);
	const lineClampHeightRef = useRef<number>(0);
	const [isLineClamped, setIsLineClamped] = useState(false);
	const [isExpanded, setIsExpanded] = useState(false);

	useEffect(() => {
		const checkForLineClamp = () => {
			if (!contentRef.current) {
				return;
			}

			const { clientHeight, scrollHeight } = contentRef.current;

			const shouldClampCollapsedState = !isExpanded && clientHeight < scrollHeight;
			const shouldClampExpandedState = isExpanded && lineClampHeightRef.current < clientHeight;
			const shouldClamp = shouldClampCollapsedState || shouldClampExpandedState;

			if (shouldClamp && !lineClampHeightRef.current) {
				lineClampHeightRef.current = clientHeight;
			}

			setIsLineClamped(shouldClamp);
		};

		checkForLineClamp();

		window.addEventListener('resize', checkForLineClamp);

		return () => window.removeEventListener('resize', checkForLineClamp);
	}, [isExpanded, isLineClamped, linesToShow]);

	const handleToggle = () => setIsExpanded((expanded) => !expanded);

	return (
		<div className={cx(styles.collapsableTextContainer, !isExpanded && styles.collapsed)}>
			<div
				ref={contentRef}
				className={styles.collapsableTextArea}
				style={{
					WebkitLineClamp: !isExpanded ? linesToShow : 'unset',
				}}
			>
				{children}
			</div>
			{isLineClamped && (
				<div className={styles.collapsableTextActions}>
					<Hyperlink inherit={true} label={isExpanded ? showLessText : showMoreText} onClick={handleToggle} />
				</div>
			)}
		</div>
	);
};
